// src/ui/components/UserManagementModal.js
import html from "./UserManagementModal.html?raw";
import { getCurrentTeamId, listTeamMembers, updateMemberRole, getCurrentUserAndProfile } from "../../services/teamService.js";
import { listTeamRoster, updateTeamMemberEmail } from "../../services/lineupService.js";
import { actions } from "../../app/state.js";
import { isAdmin } from "../../services/rbac.js";
import { onSafeBackdropClick } from "../modalUtils.js";

function $(root, selector) {
    return root.querySelector(selector);
}

let _modalInstance = null;

function createModalDOM() {
    const div = document.createElement("div");
    div.innerHTML = html;
    const template = div.querySelector("template");
    const clone = template.content.cloneNode(true);
    const root = clone.querySelector(".modal-backdrop");

    // Wire global close
    const closeBtn = $(root, "#user-mgmt-close");
    const doneBtn = $(root, "#user-mgmt-done");

    const closeFn = () => {
        if (root.parentElement) root.parentElement.removeChild(root);
        _modalInstance = null;
    };

    closeBtn?.addEventListener("click", closeFn);
    doneBtn?.addEventListener("click", closeFn);

    onSafeBackdropClick(root, closeFn);

    return { root, closeFn };
}

export async function openUserManagementModal() {
    // 1. Security Check
    if (!(await isAdmin())) {
        actions.addToast?.({ type: "error", message: "Acceso denegado. Se requieren permisos de Administrador." });
        return;
    }

    // 2. Create Modal
    const { root } = createModalDOM();
    document.body.appendChild(root);
    _modalInstance = root;

    const listContainer = $(root, "#user-mgmt-list");
    const rosterContainer = $(root, "#roster-mgmt-list");
    const { user: currentUser } = await getCurrentUserAndProfile();

    // 3. Load Data
    try {
        const teamId = await getCurrentTeamId();
        if (!teamId) throw new Error("No team ID found");

        const members = await listTeamMembers(teamId);
        renderList(listContainer, members, currentUser?.id);

        const roster = await listTeamRoster(teamId);
        renderRoster(rosterContainer, roster);

    } catch (e) {
        console.error(e);
        listContainer.innerHTML = '<div class="p-8 text-center text-red-400">Error al cargar usuarios.</div>';
    }
}

function escapeAttr(str) {
    return String(str ?? "").replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function renderRoster(container, roster) {
    if (!container) return;
    if (roster.length === 0) {
        container.innerHTML = '<div class="p-6 text-center text-slate-400 text-sm">No hay miembros en el roster.</div>';
        return;
    }

    container.innerHTML = roster.map(m => {
        const initials = (m.display_name || "?").substring(0, 2).toUpperCase();
        const linked = !!m.auth_user_id;
        return `
            <div class="flex items-center gap-3 p-3">
                <div class="w-9 h-9 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-bold text-xs shrink-0">${escapeAttr(initials)}</div>
                <div class="min-w-0 w-32 shrink-0">
                    <div class="text-sm font-bold text-slate-900 truncate">${escapeAttr(m.display_name || "—")}</div>
                    <div class="text-[10px] uppercase tracking-wider ${linked ? "text-emerald-500" : "text-amber-500"}">${linked ? "con cuenta" : "sin cuenta"}</div>
                </div>
                <input type="email" value="${escapeAttr(m.email || "")}" placeholder="email@ejemplo.com"
                    class="roster-email-input flex-1 min-w-0 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                    data-member-id="${escapeAttr(m.id)}" data-original="${escapeAttr(m.email || "")}">
                <button class="roster-email-save shrink-0 px-3 py-2 bg-slate-900 text-white text-xs font-bold rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-40"
                    data-member-id="${escapeAttr(m.id)}" disabled><i class="fa-solid fa-check"></i></button>
            </div>
        `;
    }).join("");

    // Enable save button only when the email changed
    container.querySelectorAll(".roster-email-input").forEach(input => {
        const saveBtn = container.querySelector(`.roster-email-save[data-member-id="${input.dataset.memberId}"]`);
        input.addEventListener("input", () => {
            if (saveBtn) saveBtn.disabled = input.value.trim() === (input.dataset.original || "").trim();
        });
    });

    container.querySelectorAll(".roster-email-save").forEach(btn => {
        btn.addEventListener("click", async () => {
            const memberId = btn.dataset.memberId;
            const input = container.querySelector(`.roster-email-input[data-member-id="${memberId}"]`);
            const email = input?.value?.trim() || "";
            if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                actions.addToast?.({ type: "warning", message: "Email no válido." });
                return;
            }
            btn.disabled = true;
            btn.innerHTML = '<i class="fa-solid fa-circle-notch fa-spin"></i>';
            try {
                await updateTeamMemberEmail(memberId, email);
                if (input) input.dataset.original = email;
                actions.addToast?.({ type: "success", message: "Email guardado." });
            } catch (err) {
                console.error(err);
                actions.addToast?.({ type: "error", message: "Error al guardar el email." });
            } finally {
                btn.innerHTML = '<i class="fa-solid fa-check"></i>';
            }
        });
    });
}


function renderList(container, members, currentUserId) {
    if (members.length === 0) {
        container.innerHTML = '<div class="p-8 text-center text-slate-400">No hay miembros en el equipo.</div>';
        return;
    }

    container.innerHTML = members.map(m => {
        const isMe = m.user_id === currentUserId;
        const initials = (m.display_name || "?").substring(0, 2).toUpperCase();

        // Prevent editing myself
        const disabledAttr = isMe ? "disabled title='No puedes editar tu propio rol'" : "";
        const rowClass = isMe ? "bg-slate-50/50" : "hover:bg-slate-50";

        // Role Select HTML
        const roleSelect = `
            <select class="role-select text-xs font-bold border-none bg-slate-100 rounded-lg py-1 pl-2 pr-6 focus:ring-0 cursor-pointer hover:bg-slate-200 transition-colors"
                data-user-id="${m.user_id}" ${disabledAttr}>
                <option value="admin" ${m.role === 'admin' ? 'selected' : ''}>Administrador</option>
                <option value="manager" ${m.role === 'manager' ? 'selected' : ''}>Manager</option>
                <option value="viewer" ${m.role === 'viewer' ? 'selected' : ''}>Colaborador</option>
            </select>
        `;

        return `
            <div class="flex items-center justify-between p-4 ${rowClass}">
                <div class="flex items-center gap-3 ml-2">
                    <div class="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center font-bold text-sm">
                        ${initials}
                    </div>
                    <div>
                        <div class="text-sm font-bold text-slate-900 flex items-center gap-2">
                            ${m.display_name || 'Sin nombre'}
                            ${isMe ? '<span class="text-[9px] bg-slate-200 text-slate-500 px-1 rounded uppercase tracking-wider">Tú</span>' : ''}
                        </div>
                        <div class="text-xs text-slate-500 font-mono">${m.user_id.substring(0, 8)}...</div>
                    </div>
                </div>

                <div class="flex items-center gap-4 mr-2">
                    ${roleSelect}
                    
                </div>
            </div>
        `;
    }).join("");

    // Wire Events
    // 1. Role Change
    container.querySelectorAll(".role-select").forEach(select => {
        select.addEventListener("change", async (e) => {
            const userId = e.target.dataset.userId;
            const newRole = e.target.value;
            const originalVal = e.target.getAttribute("data-original") || e.target.defaultValue; // fallback?

            // Optimistic UI? Or spinner?
            // Let's use simple toast
            try {
                await updateMemberRole(userId, newRole);
                actions.addToast?.({ type: "success", message: "Rol actualizado." });
            } catch (err) {
                console.error(err);
                actions.addToast?.({ type: "error", message: "Error al actualizar rol." });
                // Revert
                e.target.value = members.find(x => x.user_id === userId)?.role;
            }
        });
    });
}
